import { useCallback } from 'react';
import { FORM_ERROR } from 'final-form';
import { setCollectionsRecords } from '../../../../data-layer/collections/actions';
import * as collectionsService from '../../../../data-layer/collections/service';
import { showToastAsync } from '../../../../data-layer/notifications/actions';
import { useDispatch } from '../../../../data-layer/useDispatch';
import { toCollectionPersistenceModel } from '../../../../mappers/collections/toCollectionPersistenceModel';
import { CollectionPersistenceUI } from '../../../../ui/users/types';
import { contentActionsService } from '../../../../utils/ContentActionsService/ContentActionsService';
import { useEventSource } from '../../../../utils/LocationContext';
import { getPageNameByEventSource, ymTrackCollectionEdit } from '../../../../utils/metrika';
import { useBackButton } from '../../../../utils/useBackButton';
import { useCurrentUser } from '../../useCurrentUser';

interface useUpdateCollectionInfoProps {
    initialValues?: CollectionPersistenceUI;
    newValues: {
        collectionName: string;
        showInFeed: boolean;
        isPublic: boolean;
        currentSuggest?: string;
    };
}

export const useUpdateCollectionInfo = ({
    initialValues,
    newValues: { collectionName, showInFeed, isPublic, currentSuggest },
}: useUpdateCollectionInfoProps) => {
    const currentUser = useCurrentUser();
    const dispatch = useDispatch();
    const onNavigateBack = useBackButton();

    const { eventSource } = useEventSource();

    const displayName = (showInFeed ? currentSuggest : collectionName) ?? collectionName;

    const onSubmit = useCallback(async () => {
        try {
            const model = toCollectionPersistenceModel({
                ...initialValues,
                displayName,
                feedStatus: {
                    showInFeed,
                },
                isPublic,
            });
            const updatedModel = await collectionsService.updateCollection(model);

            dispatch(setCollectionsRecords(updatedModel));

            if (eventSource) {
                ymTrackCollectionEdit({
                    page: getPageNameByEventSource(eventSource),
                    userLogin: currentUser?.login,
                    businessId: currentUser?.profile?.publicId,
                    isPublic: updatedModel.isPublic,
                    name: updatedModel.displayName,
                    collectionId: updatedModel.id,
                });
                contentActionsService.trackCollectionUpdated({
                    id: updatedModel.id,
                    screen: eventSource,
                    oldValues: {
                        displayName: initialValues?.displayName,
                        showInFeed: initialValues?.feedStatus?.showInFeed,
                        isPublic: initialValues?.isPublic,
                    },
                    newValues: {
                        displayName: updatedModel.displayName,
                        showInFeed: updatedModel.feedStatus?.showInFeed,
                        isPublic: updatedModel.isPublic,
                    },
                });
            }
            let toastMessage = 'Коллекция обновлена';

            if (updatedModel.feedStatus?.showInFeed) {
                toastMessage = 'Ваша коллекция появится в рекомендациях после проверки';
            }

            dispatch(
                showToastAsync({
                    message: toastMessage,
                    actionText: 'Хорошо',
                }),
            );

            onNavigateBack();
        } catch (_error) {
            return { [FORM_ERROR]: 'Не удалось обновить коллекцию' };
        }
    }, [
        currentUser?.login,
        currentUser?.profile?.publicId,
        dispatch,
        displayName,
        eventSource,
        initialValues,
        isPublic,
        onNavigateBack,
        showInFeed,
    ]);

    return onSubmit;
};
